import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import useLenis from "../../lib/useLenis";
import { getProject, projects } from "../../data/projects";
import { optimizeImage } from "../../lib/optimizeImage";
import CaseStudyProduct from "./CaseStudyProduct";
import { FullImage, TextBlock, srcOf } from "./EditorialBlocks";

const ease = [0.16, 1, 0.3, 1];
const DEFAULT_ACCENT = "#E0685C";

const galleryOf = (p) => {
    const seen = new Set([p.cover]);
    return (p.gallery || []).filter((img) => {
        const s = srcOf(img);
        if (seen.has(s)) return false;
        seen.add(s);
        return true;
    });
};

const neighbours = (slug) => {
    const visible = projects.filter((p) => !p.hidden);
    const idx = visible.findIndex((p) => p.slug === slug);
    const n = visible.length;
    return { prev: visible[(idx - 1 + n) % n], next: visible[(idx + 1) % n] };
};

const TopBar = ({ navigate }) => (
    <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease }}
        className="fixed top-4 right-4 md:top-5 md:right-12 z-50"
    >
        <div className="flex items-center gap-1 rounded-full border border-[rgba(45,42,38,0.08)] bg-white/80 backdrop-blur-xl px-3 py-2 shadow-[0_10px_40px_-20px_rgba(45,42,38,0.25)]">
            <button
                type="button"
                onClick={() => navigate("/")}
                data-testid="case-study-back-home"
                className="flex items-center gap-2 px-2 md:px-3 py-1.5 rounded-full font-heading font-medium text-[15px] whitespace-nowrap"
            >
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-coral" />
                Mona Saedi
            </button>
            <Link
                to="/#work"
                data-testid="case-study-back-work"
                className="px-2 md:px-4 py-1.5 rounded-full text-sm font-body font-medium hover:bg-surface-1 whitespace-nowrap"
            >
                ← All work
            </Link>
            <a
                href="mailto:mona.saedi90@gmail.com"
                data-testid="case-study-cta-email"
                className="pill-btn ml-1 inline-flex items-center gap-1.5 rounded-full bg-[#2D2A26] text-cream px-4 py-2 text-sm font-body font-semibold whitespace-nowrap"
            >
                Say hi <span aria-hidden>→</span>
            </a>
        </div>
    </motion.header>
);

const TitleBlock = ({ project, color }) => (
    <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.85, ease, delay: 0.1 }}>
        <p className="font-body font-bold uppercase tracking-[0.16em] text-xs md:text-[13px]" style={{ color }}>
            {project.subtitle}
        </p>
        <h1 className="mt-3 font-heading text-4xl md:text-5xl font-semibold leading-[1.08] text-text-primary" data-testid="case-study-title">
            {project.title}
        </h1>
        <dl className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5" data-testid="case-study-meta">
            {[
                ["Client", project.client],
                ["Role", project.role],
                ["Industry", project.industry],
                ["Year", project.year],
            ]
                .filter(([, v]) => v)
                .map(([label, value]) => (
                    <div key={label}>
                        <dt className="font-body text-[11px] uppercase tracking-[0.16em] text-text-secondary">{label}</dt>
                        <dd className="mt-1 font-body text-sm md:text-[15px] text-text-primary">{value}</dd>
                    </div>
                ))}
        </dl>
        {project.external && (
            <a
                href={project.external}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="case-study-visit-live"
                className="mt-7 inline-flex items-center gap-1.5 font-body font-semibold text-sm underline underline-offset-4 decoration-1 hover:opacity-70 transition-opacity"
                style={{ color }}
            >
                Visit live site <span aria-hidden>↗</span>
            </a>
        )}
    </motion.div>
);

const Narrow = ({ children, className = "" }) => (
    <div className={`max-w-[680px] mx-auto px-6 space-y-12 md:space-y-14 ${className}`}>{children}</div>
);

const StorySections = ({ project, color }) => (
    <>
        <TextBlock label="Introduction" color={color} testid="case-study-overview">
            <p>{project.intro}</p>
            {project.secondary && <p>{project.secondary}</p>}
        </TextBlock>
        {project.audience && (
            <TextBlock label="Target Audience" color={color} testid="case-study-audience">
                <p>{project.audience}</p>
            </TextBlock>
        )}
        {project.objective && (
            <TextBlock label="Challenge" color={color} testid="case-study-challenge">
                <p>{project.objective}</p>
            </TextBlock>
        )}
        {project.solution && (
            <TextBlock label="Solution" color={color} testid="case-study-solution">
                <p>{project.solution}</p>
            </TextBlock>
        )}
        {project.roleNote && (
            <TextBlock label="My Role" color={color} testid="case-study-role">
                {project.roleTitle && <p className="font-semibold text-text-primary">{project.roleTitle}</p>}
                <p>{project.roleNote}</p>
            </TextBlock>
        )}
        {project.scope?.length > 0 && (
            <TextBlock label="Scope of Work" color={color} testid="case-study-scope">
                <p>{project.scope.join(" · ")}</p>
            </TextBlock>
        )}
        {project.tools?.length > 0 && (
            <TextBlock label="Tools" color={color} testid="case-study-tools">
                <p>{project.tools.join(" · ")}</p>
            </TextBlock>
        )}
    </>
);

const DesignSections = ({ project, color }) => {
    const items = [...(project.design || []), ...(project.approach || [])];
    if (!items.length && !project.deliverables?.length) return null;
    return (
        <Narrow className="pt-12 md:pt-16">
            {items.map((d) => (
                <TextBlock key={d.title} label={d.title} color={color} testid={`case-study-design-${d.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <p>{d.body}</p>
                </TextBlock>
            ))}
            {project.deliverables?.length > 0 && (
                <TextBlock label="Key Projects" color={color} testid="case-study-deliverables">
                    {project.deliverables.map((d) => (
                        <p key={d.title}>
                            <span className="font-semibold text-text-primary">{d.title}.</span> {d.body}
                        </p>
                    ))}
                </TextBlock>
            )}
        </Narrow>
    );
};

const SiteCases = ({ cases }) => (
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-12" data-testid="case-study-site-cases">
        {cases.map((c, i) => (
            <a
                key={c.href + i}
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`site-case-${i}`}
                className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-coral rounded-xl"
            >
                <img src={optimizeImage(c.img, 1200)} alt={c.label} loading="lazy" decoding="async" className="block w-full h-auto rounded-lg md:rounded-xl" />
                <p className="mt-3 font-body text-sm md:text-base text-text-primary group-hover:opacity-70 transition-opacity">
                    {c.label} <span aria-hidden>↗</span>
                </p>
            </a>
        ))}
    </div>
);

const NavLink = ({ to, label, title, align, testid }) => (
    <Link
        to={to}
        data-testid={testid}
        className={`group block focus:outline-none focus-visible:ring-2 focus-visible:ring-coral rounded-lg ${align}`}
    >
        <span className="block font-body text-[11px] uppercase tracking-[0.16em] text-text-secondary">{label}</span>
        {title && (
            <span className="mt-2 block font-heading text-lg md:text-2xl font-semibold text-text-primary group-hover:opacity-60 transition-opacity">
                {title}
            </span>
        )}
    </Link>
);

const ProjectNav = ({ slug }) => {
    const { prev, next } = neighbours(slug);
    return (
        <nav aria-label="Project navigation" className="max-w-[1400px] mx-auto px-6 md:px-8 py-20 md:py-28 grid grid-cols-2 md:grid-cols-3 gap-8 items-end">
            <NavLink to={`/work/${prev.slug}`} label="← Previous project" title={prev.title} align="text-left" testid="case-study-prev-project" />
            <div className="order-last col-span-2 md:order-none md:col-span-1 text-center">
                <Link
                    to="/#work"
                    data-testid="case-study-back-all-work"
                    className="inline-block font-body font-semibold text-sm md:text-base text-text-primary underline underline-offset-4 decoration-1 hover:opacity-60 transition-opacity"
                >
                    Back to work
                </Link>
            </div>
            <NavLink to={`/work/${next.slug}`} label="Next project →" title={next.title} align="text-right" testid="case-study-next-project" />
        </nav>
    );
};

const NotFound = () => (
    <main className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center">
            <h1 className="font-heading text-4xl md:text-5xl font-semibold">Project not found.</h1>
            <Link to="/" data-testid="case-study-404-back" className="mt-8 inline-block font-body font-semibold underline underline-offset-4">
                ← Back home
            </Link>
        </div>
    </main>
);

export const CaseStudy = () => {
    useLenis();
    const { slug } = useParams();
    const navigate = useNavigate();
    const project = getProject(slug);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, [slug]);

    if (project && project.variant === "product") return <CaseStudyProduct />;
    if (!project) return <NotFound />;

    const color = project.accentColor || DEFAULT_ACCENT;
    const gallery = galleryOf(project);

    return (
        <main className="relative bg-white text-text-primary overflow-x-hidden" data-testid={`case-study-${project.slug}`}>
            <TopBar navigate={navigate} />

            <div className="pt-24 md:pt-28" data-testid="case-study-hero">
                <FullImage img={project.cover} alt={project.title} testid="case-study-hero-image" />
            </div>

            <Narrow className="pt-16 md:pt-24">
                <TitleBlock project={project} color={color} />
                <StorySections project={project} color={color} />
            </Narrow>

            <div className="space-y-4 md:space-y-6 pt-14 md:pt-20" data-testid="case-study-gallery">
                {gallery.map((img, i) => (
                    <FullImage key={srcOf(img) + i} img={img} alt={`${project.title} image ${i + 1}`} />
                ))}
            </div>

            <DesignSections project={project} color={color} />

            {project.siteCases?.length > 0 && (
                <div className="pt-16 md:pt-24">
                    <SiteCases cases={project.siteCases} />
                </div>
            )}

            {project.results && (
                <Narrow className="pt-12 md:pt-16">
                    <TextBlock label="Outcome" color={color} testid="case-study-results">
                        <p>{project.results}</p>
                        {project.credits && <p className="text-sm md:text-base text-text-secondary">Credits: {project.credits}</p>}
                    </TextBlock>
                </Narrow>
            )}

            <ProjectNav slug={project.slug} />

            <footer data-testid="case-study-footer" className="py-8 font-body text-sm text-text-secondary">
                <div className="max-w-[1400px] mx-auto px-6 md:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                    <p>© {new Date().getFullYear()} Mona Saedi. All rights reserved.</p>
                    <a href="mailto:mona.saedi90@gmail.com" data-testid="case-study-footer-email" className="hover:text-text-primary transition-colors">
                        mona.saedi90@gmail.com
                    </a>
                </div>
            </footer>
        </main>
    );
};

export default CaseStudy;
